import algoliasearch from 'algoliasearch';
import {currentUser} from '@/services/FirestoreService';

const cachedKey: {
  key: string | undefined;
  expiration: number | undefined;
  inProgress: Promise<void> | false;
} = {
  key: undefined,
  expiration: undefined,
  inProgress: false,
};

/**
 * Retrieves the Algolia key used for authentication and authorization.
 * If a cached key is available and has not expired, it will be returned.
 * Otherwise, a new key will be fetched from the server.
 */
async function getAlgoliaKey() {
  if (cachedKey.inProgress) {
    await cachedKey.inProgress;
  }
  if (cachedKey.key && cachedKey.expiration && cachedKey.expiration > Date.now()) {
    return cachedKey.key;
  }

  try {
    let done;
    cachedKey.inProgress = new Promise((resolve) => (done = resolve));

    // generate the URL for the Cloud Function that retrieves the Algolia key
    const cfUrl = process.env.VUE_APP_CLOUD_FUNCTIONS_URL;
    const useProdGCP = process.env.VUE_APP_GCP_ENV === 'production';
    const tokenUrl = `${cfUrl}/retrieveMapsToken${useProdGCP ? '' : '-staging'}?retrieve=algoliaKey`;

    // get the user's ID token for authentication
    const user = currentUser();
    if (!user) {
      throw new Error('Current user not found. Cannot retrieve Algolia key.');
    }
    const idToken = await user.getIdToken();

    // make the fetch, parse the response
    const response = await fetch(tokenUrl, {method: 'GET', headers: {Authorization: `Bearer ${idToken}`}});
    const key = await response.json();

    // update the cached key and expiration
    cachedKey.key = key;
    cachedKey.expiration = Date.now() + 1000 * 60 * 60 * 1; // 1 hour
    done();
    cachedKey.inProgress = false;

    return key;
  } catch (error) {
    console.error(`Failed to retrieve Algolia key: `, error);
  }
}

/**
 * Retrieves maps for a specific page number. If a user ID is provided, only maps created by that user will be returned.
 *
 * Note: filtering by UserID requires that the Algolia index be configured with the `createdOn.uid` facet.*
 * @param {number} pageNumber - The page number of results to retrieve.
 * @param {string} [userId] - Optional. The user ID to filter maps by.
 */
async function getMapsForPageNumber(pageNumber: number, userId?: string) {
  try {
    const key = await getAlgoliaKey();

    const client = algoliasearch('1TIGF75UT0', key);

    const index = client.initIndex(process.env.VUE_APP_ALGOLIA_INDEX ?? 'dev_shareableMaps');
    const searchOptions = {
      filters: userId !== undefined ? `createdOn.uid:${userId}` : '',
      page: pageNumber,
      hitsPerPage: 20,
    };
    const res = await index.search('', searchOptions);
    return res;
  } catch (error) {
    console.error('Could not complete algolia request: ', error);
  }
}

export {
  getMapsForPageNumber,
  getAlgoliaKey,
};
